import React, { useCallback, useEffect, useRef, useState } from "react";
import Markdown from "markdown-to-jsx";
import { UserMessage } from "./UserMessage";
import { AgentMessage } from "./AgentMessage";
import { MessageAuthor } from "../../Models/MessageAuthor";
import { IChatMessage } from "../../Models/ChatMessage";
import { Link } from "react-router-dom";
import { Box, styled } from "@mui/material";
import { observer } from "mobx-react";
import type { KboFlowResearchPreview } from "services/AcxAgentService";
import { KboFlowResearchDrawerPreview } from "./KboFlowResearchDrawerPreview";

type Props = {
    author: MessageAuthor;
    fullscreen?: boolean;
    kboFlowPreview?: KboFlowResearchPreview;
    onOpenFullscreen?: () => void;
} & IChatMessage;

const INITIAL_REPORT_HEIGHT = 150;

// Each report's measured height, so a remounted chat lays out at full size straight
// away and its restored scroll position still lands in the right place.
const reportHeights = new Map<string, number>();

function KboFlowResearchReport({ html }: { html: string }) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [height, setHeight] = useState(
        () => reportHeights.get(html) ?? INITIAL_REPORT_HEIGHT,
    );

    const syncHeight = useCallback(() => {
        const document = iframeRef.current?.contentDocument;
        if (!document) return;

        const { body, documentElement } = document;
        const nextHeight = Math.ceil(
            Math.max(
                body?.scrollHeight ?? 0,
                body?.offsetHeight ?? 0,
                documentElement?.scrollHeight ?? 0,
                documentElement?.offsetHeight ?? 0,
            ),
        );

        if (nextHeight > 0) {
            reportHeights.set(html, nextHeight);
            setHeight((currentHeight) =>
                currentHeight === nextHeight ? currentHeight : nextHeight,
            );
        }
    }, [html]);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        let resizeObserver: ResizeObserver | undefined;
        const imageLoadListeners: HTMLImageElement[] = [];

        const clearDocumentObservers = () => {
            resizeObserver?.disconnect();
            resizeObserver = undefined;
            for (const image of imageLoadListeners) {
                image.removeEventListener("load", syncHeight);
            }
            imageLoadListeners.length = 0;
        };

        const observeDocument = () => {
            clearDocumentObservers();

            const document = iframe.contentDocument;
            if (!document) return;

            syncHeight();

            if (typeof ResizeObserver !== "undefined") {
                resizeObserver = new ResizeObserver(syncHeight);
                if (document.body) resizeObserver.observe(document.body);
                if (document.documentElement) {
                    resizeObserver.observe(document.documentElement);
                }
            }

            for (const image of document.images) {
                image.addEventListener("load", syncHeight);
                imageLoadListeners.push(image);
            }
        };

        iframe.addEventListener("load", observeDocument);
        observeDocument();

        return () => {
            iframe.removeEventListener("load", observeDocument);
            clearDocumentObservers();
        };
    }, [html, syncHeight]);

    return (
        <AgentMessage
            item
            className="pendo-ignore"
            sx={{
                backgroundColor: "transparent",
                display: "block",
                padding: 0,
                width: "100%",
            }}
        >
            <Box
                component="iframe"
                ref={iframeRef}
                title="KBo research report"
                srcDoc={html}
                sandbox="allow-same-origin"
                scrolling="no"
                sx={{
                    border: 0,
                    display: "block",
                    height,
                    overflow: "hidden",
                    width: "100%",
                }}
            />
        </AgentMessage>
    );
}

const UlOverride = styled("ul")(({ theme }) => ({
    paddingLeft: theme.spacing(2),
}));

const OlOverride = styled("ol")(({ theme }) => ({
    paddingLeft: theme.spacing(2),
}));

// Quoted conversation excerpts read as cards rather than indented text. A closing line that links to a
// conversation (e.g. KBo's research evidence) becomes the card's footer, with the link on the right.
const BlockquoteOverride = styled("blockquote")(({ theme }) => ({
    position: "relative",
    margin: theme.spacing(1.5, 0),
    padding: theme.spacing(2, 3, 2, 6),
    border: `1px solid ${theme.palette.gray[200]}`,
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.white.main,
    color: theme.palette.gray[800],
    "&::before": {
        content: '"\\201D"',
        position: "absolute",
        left: theme.spacing(2.5),
        // Shares the first line's box so the mark sits level with the quote's opening words.
        top: theme.spacing(2),
        fontSize: 24,
        fontWeight: 700,
        lineHeight: "24px",
        color: theme.palette.gray[400],
    },
    "& p": { margin: 0 },
    "& p + p": { marginTop: theme.spacing(1.5) },
    '& p:last-child:has(a[href^="/app/conversations/"])': {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        color: theme.palette.gray[500],
        fontSize: 14,
        "& a": {
            marginLeft: "auto",
            fontWeight: 600,
            textDecoration: "none",
            whiteSpace: "nowrap",
        },
    },
}));

function LinkOverride(props: React.PropsWithChildren<{ href: string }>) {
    return <Link className="pendo-ignore" to={props.href}>{props.children}</Link>;
}

//Tags that should be stripped from agent messages before rendering
//These are used for system instructions and should not be visible to users
const SYSTEM_TAGS = [
    'additional_instructions'
] as const;

function stripSystemTags(content: string): string {
    let processedContent = content;

    for (const tag of SYSTEM_TAGS) {
        const regex = new RegExp(`<${tag}>[\\s\\S]*?<\\/${tag}>`, 'g');
        processedContent = processedContent.replace(regex, '');
    }

    return processedContent;
}

function fixUnclosedMarkdownLinks(content: string): string {
    let processedContent = content;

    // Regular expression to find markdown links
    // Matches [text](url) pattern, including incomplete ones
    const linkRegex = /\[([^\]]*)\](\([^)]*)?$/;

    const match = content.match(linkRegex);

    if (match) {
        const partialUrl = match[2] || "(";

        // If we have an opening parenthesis but no closing one, add it
        if (partialUrl.startsWith("(") && !partialUrl.includes(")")) {
            processedContent = content + ")";
        }
    }

    return processedContent;
}

const Message: React.FC<Props> = observer((props) => {
    const isUserMessage = props.author === MessageAuthor.User;

    const MessageContent = isUserMessage ? UserMessage : AgentMessage;

    const rawContent = props.dynamicContent
        ? props.dynamicContent()
        : props.content;

    if (!isUserMessage && props.renderMode === "htmlDocument") {
        if (!props.fullscreen && props.onOpenFullscreen) {
            return (
                <AgentMessage
                    item
                    className="pendo-ignore"
                    sx={{
                        backgroundColor: "transparent",
                        display: "block",
                        padding: 0,
                        width: "100%",
                    }}
                >
                    <KboFlowResearchDrawerPreview
                        preview={props.kboFlowPreview}
                        onOpenFullscreen={props.onOpenFullscreen}
                    />
                </AgentMessage>
            );
        }

        return <KboFlowResearchReport html={rawContent} />;
    }

    const getContent = () => {
        if (isUserMessage) {
            return rawContent;
        }

        const withoutSystemTags = stripSystemTags(rawContent);
        return fixUnclosedMarkdownLinks(withoutSystemTags);
    };

    return (
        <MessageContent item className={"pendo-ignore"}>
            {!isUserMessage && (
                <Markdown
                    options={{
                        overrides: {
                            a: LinkOverride,
                            ul: UlOverride,
                            ol: OlOverride,
                            blockquote: BlockquoteOverride,
                        },
                    }}
                >
                    {getContent()}
                </Markdown>
            )}
            {isUserMessage && <React.Fragment>{getContent()}</React.Fragment>}
        </MessageContent>
    );
});

export default Message;
